import {
    type PropsWithChildren,
    createContext,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    useCallback
} from 'react';

import { io } from 'socket.io-client';

import { getUrls } from '../utils/url';

const RECONNECT_ATTEMPTS = 15;

export type OnMessageType = (funcName: string, data: any) => void;
export type SocketStatus = 'connecting' | 'connected' | 'disconnected' | 'reconnect-timeout';

export type SocketContextType = {
    addMessageHandler: (handler: OnMessageType) => void;
    removeMessageHandler: (handler: OnMessageType) => void;
    sendMessage: () => void;
    connectionId: string;
    status: SocketStatus;
};

export const SocketContext = createContext<SocketContextType>({} as any);
export const useSocket = () => useContext(SocketContext);

const { websocket: socketUrl } = getUrls();

export const SocketProvider = (props: PropsWithChildren<{}>) => {
    const { children } = props;
    const [connectionId, setConnectionId] = useState('');
    const messageHandlersRef = useRef<OnMessageType[]>([]);
    const [socketStatus, setSocketStatus] = useState<SocketStatus>('connecting');

    const socket = useMemo(
        () =>
            io(socketUrl, {
                withCredentials: true,
                closeOnBeforeunload: true,
                reconnectionAttempts: RECONNECT_ATTEMPTS
            }),
        []
    );

    const mounted = useRef(false);

    useEffect(() => {
        mounted.current = true;

        return () => {
            mounted.current = false;
        };
    }, []);

    useEffect(() => {
        if (!socket) {
            return;
        }

        const disconnectHandler = () => {
            console.warn('socket disconnected');
            setSocketStatus('disconnected');
        };

        const sessionHandler = ({ sessionId }) => {
            socket.auth = { sessionId };

            if (mounted.current) {
                console.info('component mounted, setting status to connected');
                setSocketStatus('connected');
                setConnectionId(sessionId);
            } else {
                console.warn('component not mounted, ignoring session handler');
            }
        };

        const connectErrorHandler = (error: Error) => {
            console.error('Connection error', error);
        };

        const reconnectFailedHandler = () => {
            setSocketStatus('reconnect-timeout');
        };

        const reconnectAttemptHandler = (attempt: number) => {
            console.warn(`Attempting reconnect ${attempt} of ${RECONNECT_ATTEMPTS}`);
            setSocketStatus('disconnected');
        };

        socket.connect();

        socket.on('disconnect', disconnectHandler);
        socket.on('session', sessionHandler);
        socket.on('connect_error', connectErrorHandler);
        socket.io.on('reconnect_failed', reconnectFailedHandler);
        socket.io.on('reconnect_attempt', reconnectAttemptHandler);

        return () => {
            socket.off('disconnect', disconnectHandler);
            socket.off('session', sessionHandler);
            socket.off('connect_error', connectErrorHandler);
            socket.io.off('reconnect_failed', reconnectFailedHandler);
            socket.io.off('reconnect_attempt', reconnectAttemptHandler);

            socket.disconnect();
        };
    }, [socket]);

    useEffect(() => {
        const onAnyHandler = (eventName, data) => {
            if (eventName === 'generic') {
                const { func } = data;
                const { event } = data;

                messageHandlersRef.current.forEach((handler) => {
                    handler(func, event);
                });
            }
        };

        socket.onAny(onAnyHandler);

        return () => {
            socket.offAny(onAnyHandler);
        };
    }, [socket]);

    const addMessageHandler = useCallback((handler: OnMessageType) => {
        messageHandlersRef.current.push(handler);
    }, []);

    const removeMessageHandler = useCallback((handler: OnMessageType) => {
        messageHandlersRef.current = messageHandlersRef.current.filter((h) => h !== handler);
    }, []);

    return useMemo(
        () => (
            <SocketContext.Provider
                value={{
                    addMessageHandler,
                    removeMessageHandler,
                    sendMessage: () => {},
                    connectionId,
                    status: socketStatus
                }}
            >
                {children}
            </SocketContext.Provider>
        ),
        [children, connectionId, addMessageHandler, removeMessageHandler, socketStatus]
    );
};
